import { createSlice } from '@reduxjs/toolkit';
import strings from '../../../common/Translation/Translate';
import axiosInstance from '../../axios';
import { identifiers } from '../../constants/identifiers';
import { URLS } from '../../constants/urls';
import { clearStorage, fetchFromStorage, removeFromStorage, saveToStorage } from '../../storage';

// Feature flag was removed; drop any leftover value from older clients.
removeFromStorage('allowOrganizationTimeslots');
removeFromStorage('allowOrganizationTimeslots', true);

const fromStorage = fetchFromStorage(identifiers.access_token);
const link = fetchFromStorage(identifiers.preview);

const DEFAULT_THEME = {
  primary: '#4E4848',
  secondaryTextColor: '#000000',
  primaryTextColor: '#ffffff',
  secondary: '#EFEAE2',
};

const parseStoredJson = <T>(raw: string | null, fallback: T): T => {
  if (raw == null || raw === '' || raw === 'undefined' || raw === 'null') {
    return fallback;
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed == null ? fallback : parsed;
  } catch {
    return fallback;
  }
};

const parseStoredTheme = (raw: string | null) => {
  const parsed = parseStoredJson<any>(raw, DEFAULT_THEME);
  if (typeof parsed !== 'object' || parsed == null || Array.isArray(parsed) || typeof parsed.primary !== 'string') {
    return DEFAULT_THEME;
  }
  return parsed;
};

interface AuthenticationState {
  isRefreshingContext: boolean;
  user: any;
  authenticated: boolean;
  privilege: any;
  translation: any;
  priviewLink: string | null;
  languageList: [] | null;
  selectedlanguage: any;
  selectedOrganization: any;
  orgInfo: null;
  currency: { currencyLocale: string; currency: string; currencySymbol?: string };
  theme: {
    primary: string;
    primaryTextColor: string;
    secondary: string;
    secondaryTextColor: string;
    logoBackgroundColor: string;
  };
  parentOrgInitData: {
    faviconUrl: string;
    title: string;
    logoUrl: any;
    show24HourClock: boolean;
    msClarityId?: string;
    loginTypes?: string[];
  };
  isDirectBooking: boolean;
  defaultCountryCode: string;
  serviceChargeDropdown: boolean;
  integration: string;
  showSideBar: boolean;
  isSalesManagementEnabled: boolean;
  isCaptureLoyaltyProgram: boolean;
  isEnquiriesAllowed: boolean;
}

const initialState = {
  isRefreshingContext: false,
  user: null,
  authenticated: fromStorage || false,
  privilege: fetchFromStorage(identifiers.privilegeList),
  translation: fetchFromStorage(identifiers.translation),
  priviewLink: link || '',
  languageList: parseStoredJson(fetchFromStorage(identifiers.languages), null),
  selectedlanguage: parseStoredJson(fetchFromStorage(identifiers.selectedLanguage), { value: '' }),
  selectedOrganization: parseStoredJson(fetchFromStorage('selectedOrganization'), { value: '' }),
  orgInfo: {},
  currency: parseStoredJson<AuthenticationState['currency'] | null>(fetchFromStorage(identifiers.currency), null),
  theme: parseStoredTheme(fetchFromStorage(identifiers.theme)),
  parentOrgInitData: parseStoredJson(fetchFromStorage('parentOrgInitData'), {
    faviconUrl: '',
    title: strings.bbrText,
    logoUrl: '',
    show24HourClock: false,
    msClarityId: '',
  }),
  isDirectBooking: fetchFromStorage('isDirectBooking') !== null ? fetchFromStorage('isDirectBooking') === 'true' : true,
  defaultCountryCode: fetchFromStorage('defaultCountryCode') !== null ? fetchFromStorage('defaultCountryCode') : 'IN',
  serviceChargeDropdown:
    fetchFromStorage('serviceChargeDropdown') !== null ? fetchFromStorage('serviceChargeDropdown') : false,
  integration: fetchFromStorage(identifiers.integration),

  showSideBar: true,
  isEnquiriesAllowed:
    fetchFromStorage('isEnquiriesAllowed') !== null ? fetchFromStorage('isEnquiriesAllowed') === 'true' : true,
};

const authenticationSlice = createSlice({
  name: 'authenticationSlice',
  initialState,
  reducers: {
    setRefreshingContext(state, action) {
      state.isRefreshingContext = action.payload;
    },
    /**
     * Stores just the token material + the minimal fields the login screen needs to
     * route (firstTimeLogin, organization, tenantId). Call {@link fetchUserContext}
     * afterwards to hydrate privileges, integration, currency, links, etc.
     */
    setAuthTokens(state, action) {
      if (!action.payload) {
        state.authenticated = false;
        removeFromStorage(identifiers.access_token);
        return;
      }
      const { accessToken, refreshToken, organization, tenantId } = action.payload;
      state.authenticated = true;
      saveToStorage(identifiers.access_token, accessToken);
      saveToStorage(identifiers.refresh_token, refreshToken);
      if (tenantId !== undefined && tenantId !== null) {
        saveToStorage('tenantId', tenantId);
      }
      if (organization !== undefined) {
        saveToStorage(identifiers.organization, organization === null ? '*' : JSON.stringify(organization));
      }
    },
    /**
     * Hydrates privileges, organization context and feature toggles from
     * {@code GET /authenticated/user-context}. Safe to re-dispatch any time
     * to pick up server-side changes without forcing a re-login.
     */
    setUserContext(state, action) {
      if (!action.payload) return;
      const {
        previewLink,
        currency,
        currencySymbol,
        isDirectBooking,
        integration,
        currencyLocale,
        serviceChargeDropdown,
        defaultCountryCode,
        organization,
        privileges,
        version,
        tenantId,
        isSalesManagementEnabled,
        isCaptureLoyaltyProgram,
        isEnquiriesAllowed,
      } = action.payload;
      const currencyCode =
        typeof currency === 'object' && currency !== null
          ? currency.currency || currency.code || currency.value
          : currency;
      const currencyLocaleValue =
        currencyLocale ||
        (typeof currency === 'object' && currency !== null
          ? currency.currencyLocale || currency.locale || currency.currency_locale
          : undefined);
      const currencySymbolValue =
        currencySymbol ||
        (typeof currency === 'object' && currency !== null
          ? currency.currencySymbol || currency.symbol || currency.currency_symbol
          : undefined);
      state.priviewLink = previewLink;
      state.privilege = privileges;
      state.currency = {
        currencyLocale: currencyLocaleValue,
        currency: currencyCode,
        currencySymbol: currencySymbolValue,
      };
      state.isDirectBooking = isDirectBooking;
      state.integration = integration;
      state.defaultCountryCode = defaultCountryCode || 'IN';
      state.serviceChargeDropdown = serviceChargeDropdown;
      if (isEnquiriesAllowed !== undefined) {
        state.isEnquiriesAllowed = isEnquiriesAllowed !== false;
        saveToStorage('isEnquiriesAllowed', String(isEnquiriesAllowed !== false));
      }
      saveToStorage(identifiers.preview, previewLink);
      saveToStorage(identifiers.integration, integration);
      saveToStorage('isDirectBooking', JSON.stringify(isDirectBooking));
      saveToStorage('serviceChargeDropdown', serviceChargeDropdown);
      saveToStorage('defaultCountryCode', defaultCountryCode || 'IN');
      saveToStorage(identifiers.organization, organization === null ? '*' : JSON.stringify(organization));
      saveToStorage(
        identifiers.currency,
        JSON.stringify({
          currencyLocale: currencyLocaleValue,
          currency: currencyCode,
          currencySymbol: currencySymbolValue,
        }),
      );
      saveToStorage(identifiers.privilegeList, privileges);
      if (version !== undefined) saveToStorage('version', version);
      if (tenantId !== undefined) saveToStorage('tenantId', tenantId);
      saveToStorage('isSalesManagementEnabled', isSalesManagementEnabled);
      saveToStorage('isCaptureLoyaltyProgram', isCaptureLoyaltyProgram);
    },
    /**
     * Back-compat wrapper — kept so legacy callers that pass either a slim token
     * payload, a full user-context payload, or the old fat response keep working.
     * New code should prefer {@code setAuthTokens} + {@code fetchUserContext}.
     */
    setAuthentication(state, action) {
      if (!action.payload) {
        state.authenticated = false;
        removeFromStorage(identifiers.access_token);
        return;
      }
      authenticationSlice.caseReducers.setAuthTokens(state, action);
      if ('privileges' in action.payload || 'integration' in action.payload) {
        authenticationSlice.caseReducers.setUserContext(state, action);
      }
    },
    setShowSideBar(state, action) {
      state.showSideBar = action.payload;
    },
    setSelectedOrganization(state, action) {
      saveToStorage('selectedOrganization', JSON.stringify(action.payload));
      state.selectedOrganization = action.payload;
    },
    setSelectedLanguage(state, action) {
      saveToStorage(identifiers.selectedLanguage, JSON.stringify(action.payload));
      state.selectedlanguage = action.payload;
    },
    setLanguageList(state, action) {
      saveToStorage(identifiers.languages, JSON.stringify(action.payload));
      state.languageList = action.payload;
    },
    setTheme(state, action) {
      saveToStorage(identifiers.theme, JSON.stringify(action.payload));
      state.theme = action.payload;
    },
    setParentOrgInitData(state, action) {
      saveToStorage('parentOrgInitData', JSON.stringify(action.payload));
      state.parentOrgInitData = action.payload;
    },

    setUser(state, action) {
      state.user = action.payload;
    },
    setOrgInfo(state, action) {
      state.orgInfo = action.payload;
    },
    setTranslation(state, action) {
      state.translation = action.payload;
    },
    logout(state) {
      clearStorage('all');
      state.authenticated = false;
    },
  },
});

export const refreshToken = (refreshToken?: any, onSuccess?: () => void) => async (dispatch: any) => {
  const token = fetchFromStorage(identifiers.refresh_token);
  dispatch(authenticationSlice.actions.setRefreshingContext(true));
  try {
    const req = {
      refreshToken: refreshToken || token,
    };
    const { status, data } = await axiosInstance.post(URLS.refreshToken, req);
    if (status === 200) {
      removeFromStorage(identifiers.access_token);
      // Refresh does not carry organization context. A null organization would be
      // stored as "*", and the layout shows the service-unavailable page until
      // user-context replaces it.
      const tokens = { ...data };
      if (tokens.organization == null) {
        delete tokens.organization;
      }
      dispatch(authenticationSlice.actions.setAuthTokens(tokens));
      await dispatch(fetchUserContext() as any);
      onSuccess && onSuccess();
    }
  } catch (error: any) {
    console.log(error);
  } finally {
    dispatch(authenticationSlice.actions.setRefreshingContext(false));
  }
};

/**
 * Fetches the authoritative user context (privileges, org info, feature toggles)
 * and commits it to the store. Can be dispatched any time — e.g. after login,
 * after refresh, or manually to reflect privilege changes without re-login.
 */
export const fetchUserContext = (onSuccess?: () => void) => async (dispatch: any) => {
  try {
    const { status, data } = await axiosInstance.get(URLS.userContext);
    if (status === 200) {
      dispatch(authenticationSlice.actions.setUserContext(data));
      onSuccess && onSuccess();
      return data;
    }
  } catch (error: any) {
    console.error('Failed to fetch user context', error);
    throw error;
  }
};

export const handleLogout = (refreshToken: string) => async (dispatch: any) => {
  const body: { refreshToken: string | null } = {
    refreshToken: refreshToken || fetchFromStorage(identifiers.refresh_token),
  };
  try {
    await axiosInstance.post(URLS.logout, body);
    clearStorage();
    dispatch(authenticationSlice.actions.setAuthentication(null));
  } catch (error: any) {
    clearStorage();
    console.error(error);
  }
};

export const getOrganizationList = () => async (dispatch: any) => {
  try {
    const { status, data } = await axiosInstance.get(URLS.getOrganizations);
    if (status === 200) {
      dispatch(authenticationSlice.actions.setOrgInfo(data));
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const handleLanguageChange = (lang: any) => async (dispatch: any) => {
  try {
    const { status, data } = await axiosInstance.get(URLS.tarnslation(lang.value));
    if (status === 200) {
      dispatch(authenticationSlice.actions.setSelectedLanguage(lang));
      dispatch(authenticationSlice.actions.setTranslation(data));
      strings.setContent({
        [lang]: data,
      });
    }
  } catch (error) {
    console.error(error);
    return error;
  }
};

export default authenticationSlice.reducer;

export const {
  setAuthentication,
  setAuthTokens,
  setUserContext,
  setOrgInfo,
  logout,
  setUser,
  setTranslation,
  setLanguageList,
  setSelectedOrganization,
  setTheme,
  setSelectedLanguage,
  setParentOrgInitData,
  setShowSideBar,
} = authenticationSlice.actions;

export const User = (state: { authenticationSlice: AuthenticationState }) => state.authenticationSlice.user;
export const getTheme = (state: { authenticationSlice: AuthenticationState }) => state.authenticationSlice.theme;
export const Privilege = (state: { authenticationSlice: AuthenticationState }) => state.authenticationSlice.privilege;
export const translation = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.translation;
export const languages = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.languageList;
export const organizationInfo = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.orgInfo;
export const selectedOrgId = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.selectedOrganization;
export const defaultLanguage = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.selectedlanguage;
export const Authenticated = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.authenticated;
export const isRefreshingContext = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.isRefreshingContext;
export const priviewLink = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.priviewLink;
export const getCurrency = (state: { authenticationSlice: AuthenticationState }) => state.authenticationSlice.currency;
export const parentOrgInitData = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.parentOrgInitData;
export const isDirectBooking = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.isDirectBooking;
export const defaultCountryCode = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.defaultCountryCode;
export const selectIntegration = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.integration;

export const ShowSideBar = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.showSideBar;
export const isEnquiriesAllowed = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.isEnquiriesAllowed;
